import { useMemo, useState } from "react";
import { CalendarPlus, RefreshCw, Search } from "lucide-react";

import { FormNotice } from "@/components/forms/FormNotice";
import {
  useAdminCourseAccessTimeline,
  useAdminExtendCourseAccess,
} from "@/hooks/useAdminCourseAccess";
import type { AdminCourseAccessTimelineRow } from "@/lib/schema";

import { AdminPanelHeader } from "./AdminPanelHeader";

const dateFormatter = new Intl.DateTimeFormat("en-US", {
  dateStyle: "medium",
});

const dateTimeFormatter = new Intl.DateTimeFormat("en-US", {
  dateStyle: "medium",
  timeStyle: "short",
});

function formatDateTime(value: string | null | undefined) {
  if (!value) return "No expiry";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : dateTimeFormatter.format(date);
}

function formatDaysRemaining(value: string | null | undefined) {
  if (!value) return "Lifetime access";

  const expiry = new Date(value).getTime();
  if (Number.isNaN(expiry)) return "Unknown timeline";

  const days = Math.ceil((expiry - Date.now()) / (24 * 60 * 60 * 1000));
  if (days < 0) return `Expired ${Math.abs(days)} day${Math.abs(days) === 1 ? "" : "s"} ago`;
  if (days === 0) return "Expires today";
  return `${days} day${days === 1 ? "" : "s"} left`;
}

function getLearnerLabel(row: AdminCourseAccessTimelineRow) {
  return row.user_display_name || row.user_email || row.user_id;
}

function getAccessStatus(row: AdminCourseAccessTimelineRow) {
  if (row.is_active === false) {
    return {
      label: "Inactive",
      classes: "bg-gray-100 text-gray-600",
      canExtend: row.expires_at !== null,
    };
  }

  if (!row.expires_at) {
    return {
      label: "No expiry",
      classes: "bg-green-100 text-green-700",
      canExtend: false,
    };
  }

  const expiry = new Date(row.expires_at).getTime();
  if (!Number.isNaN(expiry) && expiry <= Date.now()) {
    return {
      label: "Expired",
      classes: "bg-red-100 text-red-700",
      canExtend: true,
    };
  }

  return {
    label: "Active",
    classes: "bg-blue-100 text-blue-700",
    canExtend: true,
  };
}

function getSearchText(row: AdminCourseAccessTimelineRow) {
  return [
    row.course_name,
    row.user_display_name,
    row.user_email,
    row.user_id,
    row.course_id,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
}

export function AccessExtensionsPanel() {
  const timeline = useAdminCourseAccessTimeline();
  const extendAccess = useAdminExtendCourseAccess();

  const [query, setQuery] = useState("");
  const [editingPermissionId, setEditingPermissionId] = useState<string | null>(null);
  const [daysInput, setDaysInput] = useState("30");
  const [extendingId, setExtendingId] = useState<string | null>(null);
  const [notice, setNotice] = useState<{ tone: "success" | "error"; message: string } | null>(null);

  const rows = (timeline.data ?? []) as AdminCourseAccessTimelineRow[];
  const filteredRows = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    if (!normalizedQuery) return rows;
    return rows.filter((row) => getSearchText(row).includes(normalizedQuery));
  }, [query, rows]);

  const parsedDays = Number(daysInput);
  const daysAreValid = Number.isInteger(parsedDays) && parsedDays >= 1 && parsedDays <= 3650;
  const isRefreshing = timeline.loading;

  const refresh = () => {
    setNotice(null);
    void timeline.refetch();
  };

  const handleStartExtend = (row: AdminCourseAccessTimelineRow) => {
    setNotice(null);
    setEditingPermissionId(row.permission_id);
    setDaysInput("30");
  };

  const handleExtend = async (row: AdminCourseAccessTimelineRow) => {
    if (!daysAreValid) {
      setNotice({ tone: "error", message: "Enter extension days between 1 and 3650." });
      return;
    }

    setNotice(null);
    setExtendingId(row.permission_id);
    const result = await extendAccess.execute(row.permission_id, parsedDays);
    setExtendingId(null);

    if (result.error) {
      setNotice({ tone: "error", message: result.error });
      return;
    }

    setEditingPermissionId(null);
    await timeline.refetch();
    const newExpiry = result.data?.new_expires_at
      ? dateFormatter.format(new Date(result.data.new_expires_at))
      : "the new expiry date";
    setNotice({
      tone: "success",
      message: `${row.course_name} access for ${getLearnerLabel(row)} was extended to ${newExpiry}.`,
    });
  };

  return (
    <div className="space-y-6">
      <AdminPanelHeader
        inset
        title="Access Extensions"
        description="Extend a learner's existing tutorial timeline for free. This only updates course access and does not create Stripe purchase records."
        action={
          <button
            type="button"
            onClick={refresh}
            disabled={isRefreshing}
            className="inline-flex items-center gap-2 rounded-xl border border-[#e5d7e0] bg-white px-4 py-2 text-sm font-semibold text-[#5d2449] transition-colors hover:bg-[#fbf4f8] disabled:opacity-50"
          >
            <RefreshCw className={`h-4 w-4 ${isRefreshing ? "animate-spin" : ""}`} />
            Refresh
          </button>
        }
      />

      {notice ? (
        <FormNotice message={notice.message} tone={notice.tone} role={notice.tone === "success" ? "status" : "alert"} />
      ) : null}

      {timeline.error ? (
        <FormNotice message={`Could not load tutorial access rows: ${timeline.error}`} />
      ) : null}

      <section className="rounded-[1.5rem] border border-gray-200 bg-white p-5 shadow-[0_14px_40px_rgba(31,10,24,0.05)]">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Tutorial Timelines</h3>
            <p className="mt-1 text-sm text-gray-500">
              {rows.length} access record{rows.length === 1 ? "" : "s"} found.
            </p>
          </div>

          <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
            <label className="relative block w-full sm:w-80">
              <span className="sr-only">Search learners or tutorials</span>
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <input
                type="search"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Search learners or tutorials"
                className="w-full rounded-xl border border-gray-300 bg-white py-2 pl-9 pr-3 text-sm placeholder:text-gray-400 focus:border-pink-dark focus:outline-none focus:ring-1 focus:ring-pink-dark"
              />
            </label>
          </div>
        </div>

        <div className="mt-5 overflow-hidden rounded-xl border border-gray-200">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[760px] text-left text-sm">
              <thead className="border-b bg-gray-50 text-xs uppercase tracking-[0.08em] text-gray-500">
                <tr>
                  <th className="w-[27%] px-4 py-3 text-left">Learner</th>
                  <th className="w-[29%] px-4 py-3 text-left">Tutorial</th>
                  <th className="w-[24%] px-4 py-3 text-left">Timeline</th>
                  <th className="w-[20%] px-4 py-3 text-left">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {isRefreshing ? (
                  <tr>
                    <td colSpan={4} className="px-4 py-10 text-center text-gray-400">
                      Loading tutorial access…
                    </td>
                  </tr>
                ) : filteredRows.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="px-4 py-10 text-center text-gray-400">
                      {query.trim() ? "No access rows match this search." : "No tutorial access rows found."}
                    </td>
                  </tr>
                ) : (
                  filteredRows.map((row) => {
                    const status = getAccessStatus(row);
                    const isExtending = extendingId === row.permission_id;
                    const isEditing = editingPermissionId === row.permission_id;

                    return (
                      <tr key={row.permission_id} className="align-top hover:bg-gray-50">
                        <td className="px-4 py-3">
                          <div className="font-semibold text-gray-900">{getLearnerLabel(row)}</div>
                          {row.user_email && row.user_email !== getLearnerLabel(row) ? (
                            <div className="mt-1 text-xs text-gray-500">{row.user_email}</div>
                          ) : null}
                        </td>
                        <td className="px-4 py-3">
                          <div className="font-semibold text-gray-900">{row.course_name}</div>
                          <div className="mt-1 text-xs text-gray-500">Added {formatDateTime(row.created_at)}</div>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold ${status.classes}`}>
                            {status.label}
                          </span>
                          <div className="mt-2 text-xs text-gray-500">{formatDateTime(row.expires_at)}</div>
                          <div className="mt-0.5 text-xs text-gray-400">{formatDaysRemaining(row.expires_at)}</div>
                        </td>
                        <td className="px-4 py-3">
                          {isEditing ? (
                            <div className="flex flex-col gap-2">
                              <label className="block">
                                <span className="sr-only">Extension days for {row.course_name}</span>
                                <input
                                  type="number"
                                  min={1}
                                  max={3650}
                                  step={1}
                                  value={daysInput}
                                  onChange={(event) => setDaysInput(event.target.value)}
                                  aria-label={`Extension days for ${row.course_name}`}
                                  className="w-28 rounded-xl border border-gray-300 bg-white px-3 py-2 text-sm focus:border-pink-dark focus:outline-none focus:ring-1 focus:ring-pink-dark"
                                  autoFocus
                                />
                              </label>
                              <div className="flex flex-wrap gap-2">
                                <button
                                  type="button"
                                  onClick={() => void handleExtend(row)}
                                  disabled={!daysAreValid || extendAccess.loading}
                                  aria-label={`Confirm extension for ${row.course_name}`}
                                  className="inline-flex items-center justify-center rounded-full border border-[#762a5c] bg-[#762a5c] px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-[#63224d] disabled:cursor-not-allowed disabled:opacity-50"
                                >
                                  {isExtending ? "Saving…" : "Confirm"}
                                </button>
                                <button
                                  type="button"
                                  onClick={() => setEditingPermissionId(null)}
                                  disabled={extendAccess.loading}
                                  className="inline-flex items-center justify-center rounded-full border border-gray-200 bg-white px-3 py-1.5 text-xs font-semibold text-gray-600 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                                >
                                  Cancel
                                </button>
                              </div>
                            </div>
                          ) : (
                            <button
                              type="button"
                              onClick={() => handleStartExtend(row)}
                              disabled={!status.canExtend || extendAccess.loading}
                              className="inline-flex min-w-[8.5rem] items-center justify-center gap-1.5 rounded-full border border-[#762a5c] bg-white px-3 py-1.5 text-xs font-semibold text-[#762a5c] transition-colors hover:bg-[#faf1f6] disabled:cursor-not-allowed disabled:border-gray-200 disabled:text-gray-400 disabled:hover:bg-white"
                            >
                              <CalendarPlus className="h-3.5 w-3.5" />
                              {status.canExtend ? "Extend Free" : "No expiry"}
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </section>
    </div>
  );
}

export default AccessExtensionsPanel;
